<template>
  <v-container fluid dark>
    <v-card dark>
      <v-card-title> Rechnungsstatistiken </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="startDate" label="Startdatum" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="startDate" @input="startDateMenu = false"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="4">
            <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="endDate" label="Enddatum" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="endDate" @input="endDateMenu = false"></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete v-model="selectedStudio" :items="studios" item-text="name" item-value="_id" label="Studio" clearable></v-autocomplete>
          </v-col>
          <v-col cols="12" md="4">
            <v-autocomplete
              v-model="selectedInvoiceIssuer"
              :items="invoiceIssuers"
              item-text="title"
              item-value="_id"
              label="Rechnungssteller"
              clearable></v-autocomplete
          ></v-col>
          <v-col cols="12">
            <v-btn color="primary" @click="fetchStatistics"> Statistiken abrufen </v-btn>
          </v-col>
        </v-row>

        <v-card class="mt-5 pa-4" outlined>
          <div class="text-h6">Gesamtbetrag im ausgewählten Zeitraum:</div>
          <div class="text-h4 font-weight-bold primary--text">{{ grandTotal.toFixed(2) }} CHF</div>
        </v-card>

        <v-row class="mt-5">
          <v-col cols="12" md="6">
            <apexchart type="bar" :options="chartOptionsBar" :series="seriesBar"></apexchart>
          </v-col>
          <v-col cols="12" md="6">
            <apexchart type="pie" :options="chartOptionsPie" :series="seriesPie"></apexchart>
          </v-col>
        </v-row>

        <v-data-table :headers="headers" :items="statistics" :loading="loading" class="elevation-1 mt-5">
          <template v-slot:item.totalAmount="{ item }"> {{ item.totalAmount.toFixed(2) }} CHF </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from "@/api"

import { mapState } from "vuex"

export default {
  name: "DocumentStatistics",
  components: {},
  data() {
    return {
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
      selectedStudio: null,
      selectedInvoiceIssuer: null,
      studios: [],
      invoiceIssuers: [],
      statistics: [],
      loading: false,
      headers: [
        { text: "Studio", value: "studio.name" },
        { text: "Rechnungssteller", value: "invoiceIssuer.title" },
        { text: "Total Betrag", value: "totalAmount" },
        { text: "Anzahl Rechnungen", value: "count" },
      ],
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),

    grandTotal() {
      return this.statistics.reduce((total, item) => total + item.totalAmount, 0)
    },

    studioStatistics() {
      const studioTotals = {}
      this.statistics.forEach((item) => {
        const studioName = item.studio?.name || "Unbekannt"
        if (studioTotals[studioName]) {
          studioTotals[studioName] += item.totalAmount
        } else {
          studioTotals[studioName] = item.totalAmount
        }
      })
      return studioTotals
    },

    issuerStatistics() {
      const issuerTotals = {}
      this.statistics.forEach((item) => {
        const issuerTitle = item.invoiceIssuer?.title || "Unbekannt"
        if (issuerTotals[issuerTitle]) {
          issuerTotals[issuerTitle] += item.totalAmount
        } else {
          issuerTotals[issuerTitle] = item.totalAmount
        }
      })
      return issuerTotals
    },

    chartOptionsBar() {
      return {
        chart: {
          id: "studios-bar",
          foreColor: "#fff",
        },
        xaxis: {
          categories: Object.keys(this.studioStatistics),
        },
        title: {
          text: "Totalbetrag pro Studio",
          style: {
            color: "#fff",
          },
        },
        theme: {
          mode: "dark",
        },
      }
    },
    seriesBar() {
      return [
        {
          name: "Total Betrag",
          data: Object.values(this.studioStatistics).map((v) => v.toFixed(2)),
        },
      ]
    },
    chartOptionsPie() {
      return {
        chart: {
          id: "issuers-pie",
          foreColor: "#fff",
        },
        labels: Object.keys(this.issuerStatistics),
        title: {
          text: "Verteilung nach Rechnungssteller",
          style: {
            color: "#fff",
          },
        },
        theme: {
          mode: "dark",
        },
        legend: {
          position: "bottom",
        },
      }
    },
    seriesPie() {
      return Object.values(this.issuerStatistics)
    },
  },
  methods: {
    async fetchStatistics() {
      this.loading = true
      try {
        const params = {
          startDate: this.startDate,
          endDate: this.endDate,
          studio: this.selectedStudio,
          invoiceIssuer: this.selectedInvoiceIssuer,
        }
        const response = await api.documents.fetchStatistics(params)
        this.statistics = response
      } catch (error) {
        console.error("Fehler beim Abrufen der Statistiken:", error)
        this.$toast.error("Fehler beim Abrufen der Statistiken")
      } finally {
        this.loading = false
      }
    },
    async fetchInitialData() {
      try {
        this.studios = this.storeStudios.map((s) => ({
          _id: s._id,
          name: s.name,
        }))
        const issuers = await api.invoiceIssuers.fetch()
        this.invoiceIssuers = issuers
      } catch (error) {
        console.error("Fehler beim Laden der initialen Daten:", error)
      }
    },
  },
  created() {
    this.fetchInitialData()
  },
}
</script>
